import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  ClipboardCheck,
  Plus,
  Trash2,
  Download,
  CheckCircle2,
  AlertTriangle,
  Clock,
  FileText,
  Loader2,
  Building2,
} from 'lucide-react';

/*
 * v1.1.6 — Verbali ASL/ASP & Prescrizioni
 * CRUD verbali di ispezione + ottemperanza con PDF di prova.
 */

const STATUS_BADGE = {
  aperta: { label: 'Aperta', cls: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock },
  ottemperata: { label: 'Ottemperata', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  scaduta: { label: 'SCADUTA', cls: 'bg-red-100 text-red-800 border-red-300', icon: AlertTriangle },
};

export default function VerbaliAslPage() {
  const [verbali, setVerbali] = useState([]);
  const [aziende, setAziende] = useState([]);
  const [pdvList, setPdvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ aperta: 0, ottemperata: 0, scaduta: 0, total: 0 });

  // Create dialog
  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    ente_ispettore: 'ASL ',
    numero_verbale: '',
    data_ispezione: new Date().toISOString().slice(0, 10),
    data_consegna: new Date().toISOString().slice(0, 10),
    giorni_ottemperanza: 30,
    azienda_id: '',
    ul_id: '',
    note: '',
    pdf: null,
  });

  // Ottempera dialog
  const [openOttempera, setOpenOttempera] = useState(null);
  const [ottemperaForm, setOttemperaForm] = useState({ note_chiusura: '', pdf: null });
  const [ottemperando, setOttemperando] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [vRes, aRes, pRes, sRes] = await Promise.all([
        api.get('/verbali-asl'),
        api.get('/aziende').catch(() => ({ data: [] })),
        api.get('/pdv').catch(() => ({ data: [] })),
        api.get('/verbali-asl/_stats/summary').catch(() => ({ data: stats })),
      ]);
      setVerbali(vRes.data || []);
      setAziende(aRes.data || []);
      setPdvList(pRes.data || []);
      setStats(sRes.data || stats);
    } catch (e) {
      toast.error('Errore nel caricamento verbali');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submitCreate = async () => {
    if (!form.ente_ispettore.trim()) {
      toast.error('Indica l&apos;ente ispettore (es. ASL Salerno)');
      return;
    }
    if (!form.pdf) {
      toast.error('Carica il PDF del verbale');
      return;
    }
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append('ente_ispettore', form.ente_ispettore.trim());
      fd.append('data_ispezione', form.data_ispezione);
      fd.append('data_consegna', form.data_consegna);
      fd.append('giorni_ottemperanza', String(form.giorni_ottemperanza));
      if (form.numero_verbale) fd.append('numero_verbale', form.numero_verbale);
      if (form.azienda_id) fd.append('azienda_id', form.azienda_id);
      if (form.ul_id) fd.append('ul_id', form.ul_id);
      if (form.note) fd.append('note', form.note);
      fd.append('pdf_verbale', form.pdf);
      await api.post('/verbali-asl', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Verbale registrato. Riceverai notifiche email a -7gg, -3gg e -1gg dalla scadenza.');
      setOpenCreate(false);
      setForm({
        ente_ispettore: 'ASL ',
        numero_verbale: '',
        data_ispezione: new Date().toISOString().slice(0, 10),
        data_consegna: new Date().toISOString().slice(0, 10),
        giorni_ottemperanza: 30,
        azienda_id: '',
        ul_id: '',
        note: '',
        pdf: null,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const submitOttempera = async () => {
    if (!ottemperaForm.pdf) {
      toast.error('Carica il PDF di prova ottemperanza');
      return;
    }
    setOttemperando(true);
    try {
      const fd = new FormData();
      if (ottemperaForm.note_chiusura) fd.append('note_chiusura', ottemperaForm.note_chiusura);
      fd.append('pdf_prova', ottemperaForm.pdf);
      await api.post(`/verbali-asl/${openOttempera.id}/ottempera`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Verbale marcato come ottemperato');
      setOpenOttempera(null);
      setOttemperaForm({ note_chiusura: '', pdf: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore ottemperanza');
    } finally {
      setOttemperando(false);
    }
  };

  const deleteVerbale = async (v) => {
    if (!window.confirm(`Eliminare il verbale "${v.ente_ispettore}" del ${v.data_consegna}? Anche i PDF allegati saranno rimossi.`)) return;
    try {
      await api.delete(`/verbali-asl/${v.id}`);
      toast.success('Verbale eliminato');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const downloadFile = async (verbaleId, kind) => {
    try {
      const url = `/verbali-asl/${verbaleId}/${kind === 'verbale' ? 'pdf-verbale' : 'pdf-prova'}`;
      const r = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${kind}_${verbaleId}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error('Download fallito');
    }
  };

  const daysLeft = (iso) => {
    if (!iso) return null;
    const sc = new Date(iso);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    sc.setHours(0, 0, 0, 0);
    return Math.round((sc - today) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="verbali-asl-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-orange-600 flex items-center justify-center shadow">
            <ClipboardCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Verbali ASL & Prescrizioni</h1>
            <p className="text-sm text-slate-500">Carica verbali ASL/ASP/NAS, gestisci scadenze ottemperanza</p>
          </div>
        </div>
        <Button
          onClick={() => setOpenCreate(true)}
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
          data-testid="new-verbale-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuovo verbale
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Aperti" n={stats.aperta} color="text-amber-700" bg="bg-amber-50" />
        <StatCard label="Ottemperati" n={stats.ottemperata} color="text-emerald-700" bg="bg-emerald-50" />
        <StatCard label="Scaduti" n={stats.scaduta} color="text-red-700" bg="bg-red-50" pulse />
        <StatCard label="Totale" n={stats.total} color="text-slate-700" bg="bg-slate-50" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento...
        </div>
      ) : verbali.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Nessun verbale registrato.</p>
            <p className="text-xs text-slate-400 mt-1">Clicca &quot;Nuovo verbale&quot; per iniziare.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="verbali-list">
          {verbali.map((v) => {
            const cfg = STATUS_BADGE[v.status] || STATUS_BADGE.aperta;
            const Icon = cfg.icon;
            const dl = daysLeft(v.scadenza_ottemperanza);
            const urgent = v.status === 'aperta' && dl !== null && dl <= 7;
            return (
              <Card key={v.id} className={`border ${urgent ? 'border-amber-400 shadow-amber-100 shadow-md' : 'border-slate-200'}`} data-testid={`verbale-row-${v.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between flex-wrap gap-3">
                    <div className="flex-1 min-w-[280px]">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-slate-900">{v.ente_ispettore}</span>
                        {v.numero_verbale && (
                          <Badge variant="outline" className="text-xs">N° {v.numero_verbale}</Badge>
                        )}
                        <Badge className={`text-xs ${cfg.cls} border`}>
                          <Icon className="w-3 h-3 mr-1" /> {cfg.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-600 space-y-0.5">
                        {v.azienda_nome && (
                          <div className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            <span>{v.azienda_nome}{v.ul_nome ? ` → ${v.ul_nome}` : ''}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-slate-400">Ispezione:</span> {v.data_ispezione}
                          {' · '}
                          <span className="text-slate-400">Consegna:</span> {v.data_consegna}
                          {' · '}
                          <span className="text-slate-400">Ottemperanza:</span> {v.giorni_ottemperanza}gg
                        </div>
                        <div className="font-medium">
                          <span className="text-slate-400">Scadenza:</span>{' '}
                          <span className={v.status === 'scaduta' ? 'text-red-700 font-bold' : urgent ? 'text-amber-700 font-bold' : 'text-slate-700'}>
                            {v.scadenza_ottemperanza}
                            {v.status === 'aperta' && dl !== null && (
                              <span className="ml-2 text-xs">
                                ({dl >= 0 ? `mancano ${dl} giorn${dl === 1 ? 'o' : 'i'}` : `scaduto da ${-dl} giorn${dl === -1 ? 'o' : 'i'}`})
                              </span>
                            )}
                          </span>
                        </div>
                        {v.note && <div className="text-slate-500 italic">📝 {v.note}</div>}
                        {v.note_chiusura && <div className="text-emerald-700">✅ Chiusura: {v.note_chiusura}</div>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => downloadFile(v.id, 'verbale')} data-testid={`download-verbale-${v.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Verbale
                      </Button>
                      {v.pdf_prova_filename && (
                        <Button size="sm" variant="outline" onClick={() => downloadFile(v.id, 'prova')} data-testid={`download-prova-${v.id}`}>
                          <Download className="w-3 h-3 mr-1" /> Prova
                        </Button>
                      )}
                      {v.status !== 'ottemperata' && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setOpenOttempera(v)}
                          data-testid={`ottempera-btn-${v.id}`}
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Ottempera
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => deleteVerbale(v)} data-testid={`delete-verbale-${v.id}`}>
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nuovo verbale ASL/ASP</DialogTitle>
            <DialogDescription>
              Carica il PDF del verbale, indica i giorni di ottemperanza concessi dall&apos;ente.
              Riceverai email automatiche a -7, -3 e -1 giorni dalla scadenza.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>Ente ispettore *</Label>
              <Input
                value={form.ente_ispettore}
                onChange={(e) => setForm({ ...form, ente_ispettore: e.target.value })}
                placeholder="es. ASL Salerno, ASP Catania, NAS Napoli"
                data-testid="form-ente"
              />
            </div>
            <div>
              <Label>Numero verbale</Label>
              <Input
                value={form.numero_verbale}
                onChange={(e) => setForm({ ...form, numero_verbale: e.target.value })}
                placeholder="2026/0042"
                data-testid="form-numero"
              />
            </div>
            <div>
              <Label>Giorni ottemperanza *</Label>
              <Input
                type="number"
                min="1"
                max="3650"
                value={form.giorni_ottemperanza}
                onChange={(e) => setForm({ ...form, giorni_ottemperanza: parseInt(e.target.value || '0', 10) })}
                data-testid="form-giorni"
              />
            </div>
            <div>
              <Label>Data ispezione *</Label>
              <Input
                type="date"
                value={form.data_ispezione}
                onChange={(e) => setForm({ ...form, data_ispezione: e.target.value })}
                data-testid="form-data-ispezione"
              />
            </div>
            <div>
              <Label>Data consegna verbale *</Label>
              <Input
                type="date"
                value={form.data_consegna}
                onChange={(e) => setForm({ ...form, data_consegna: e.target.value })}
                data-testid="form-data-consegna"
              />
            </div>
            <div>
              <Label>Azienda</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={form.azienda_id}
                onChange={(e) => setForm({ ...form, azienda_id: e.target.value, ul_id: '' })}
                data-testid="form-azienda"
              >
                <option value="">— Nessuna —</option>
                {aziende.map((a) => (
                  <option key={a.id || a._id} value={a.id || a._id}>
                    {a.denominazione || a.ragione_sociale || a.nome || '—'}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>Unità Locale</Label>
              <select
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={form.ul_id}
                onChange={(e) => setForm({ ...form, ul_id: e.target.value })}
                data-testid="form-ul"
              >
                <option value="">— Nessuna —</option>
                {pdvList
                  .filter((p) => !form.azienda_id || p.azienda_id === form.azienda_id)
                  .map((p) => (
                    <option key={p.id || p._id} value={p.id || p._id}>
                      {p.name || p.nome || p.denominazione || '—'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Note / Prescrizioni</Label>
              <Textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="es. Pulizia frigoriferi, revisione manuale HACCP, ecc."
                data-testid="form-note"
              />
            </div>
            <div className="md:col-span-2">
              <Label>PDF verbale *</Label>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setForm({ ...form, pdf: e.target.files[0] || null })}
                data-testid="form-pdf"
              />
              {form.pdf && (
                <p className="text-xs text-slate-500 mt-1">
                  Selezionato: {form.pdf.name} ({Math.round(form.pdf.size / 1024)} KB)
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} disabled={creating}>
              Annulla
            </Button>
            <Button onClick={submitCreate} disabled={creating} className="bg-red-600 hover:bg-red-700" data-testid="form-submit">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvataggio...</> : 'Registra verbale'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ottempera Dialog */}
      <Dialog open={!!openOttempera} onOpenChange={(o) => !o && setOpenOttempera(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ottempera verbale</DialogTitle>
            <DialogDescription>
              {openOttempera && (
                <>
                  <b>{openOttempera.ente_ispettore}</b> — scadenza {openOttempera.scadenza_ottemperanza}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Note di chiusura</Label>
              <Textarea
                rows={3}
                value={ottemperaForm.note_chiusura}
                onChange={(e) => setOttemperaForm({ ...ottemperaForm, note_chiusura: e.target.value })}
                placeholder="es. Pulizia eseguita il 28/02/2026 da impresa XYZ"
                data-testid="ottempera-note"
              />
            </div>
            <div>
              <Label>PDF prova di ottemperanza *</Label>
              <Input
                type="file"
                accept="application/pdf,.pdf"
                onChange={(e) => setOttemperaForm({ ...ottemperaForm, pdf: e.target.files[0] || null })}
                data-testid="ottempera-pdf"
              />
              <p className="text-xs text-slate-500 mt-1">
                Es. fatture imprese pulizia, foto post-intervento, certificati, lettera all&apos;ASL, ecc.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenOttempera(null)} disabled={ottemperando}>
              Annulla
            </Button>
            <Button onClick={submitOttempera} disabled={ottemperando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ottempera-submit">
              {ottemperando ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Ottemperando...</> : 'Conferma ottemperanza'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, n, color, bg, pulse }) {
  return (
    <div className={`rounded-xl ${bg} p-4 ${pulse && n > 0 ? 'ring-2 ring-red-300 animate-pulse' : ''}`}>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{n}</div>
    </div>
  );
}
